export const timelyPrivacy = `# Timely365 Privacy Policy

Last updated: November 3, 2026

Timely365 is an appointment booking and scheduling service operated by **Micro Logic LLC** ("we", "us", or "our"). This policy explains how we handle information when you use [timely365.com](https://timely365.com) and its connected services.

## Information we collect and why

- **Account information:** your name, email address, phone number, sign-in information, and account or staff role, to create and manage your account.
- **Booking information:** appointment dates, times, services, availability, customer contact details, and information you submit with a booking, to provide scheduling and appointment management.
- **Business information:** business profiles, staff assignments, service descriptions, and booking settings, to operate your booking pages.
- **Communications:** support requests and appointment messages, to respond to you and deliver requested notifications.
- **Technical information:** browser and device information, service logs, and session information, to operate the service, troubleshoot issues, and protect accounts.
- **Payment information:** where paid features are used, payment providers process payment details; we use transaction and subscription information to manage billing.

## Google Sign-In and Google Calendar

Google Sign-In and Calendar access are separate choices. Sign-in uses Google account identity information, such as your name, email address, and profile picture, to authenticate you.

If you choose to connect Google Calendar, Timely365 requests **read-only Calendar access** using the calendar.readonly permission. The integration retrieves events from your primary calendar for the date range being displayed. Event information can include titles, descriptions, locations, start and end times, time zones, and event identifiers. This lets you see Google events alongside Timely365 appointments and review your schedule.

This connection does not grant Timely365 permission to create, edit, or delete Google Calendar events, or to read your Gmail messages.

We store Google access and refresh tokens, token expiration information, and the connection's association with your staff account so the service can retrieve events without asking you to reconnect each time. Retrieved event information is processed by our server and browser application to display your calendar.

Google user data is used for the Calendar and sign-in features you choose. We do not sell it, use it for advertising, or use it to train generalized AI models. We limit sharing to what is necessary to provide these features with your consent, protect security, or comply with law. Human access is limited to your affirmative permission, necessary security investigations, or legal requirements.

Timely365's use and transfer of information received from Google APIs will adhere to the [Google API Services User Data Policy](https://developers.google.com/terms/api-services-user-data-policy), including its Limited Use requirements.

## AI assistants and other connected apps

You can choose to connect an app, such as ChatGPT, Claude, or AgentCrafty, to your Timely365 account. The app asks for permission on a Timely365 page, and nothing is shared unless you sign in and click **Allow**.

Once allowed, the app acts as you and can do only what your account can do. It can see your workspaces, booking types, working hours, **your own** bookings (including the name, email, phone number, and notes of people booked with you), and contact details of people who have booked with you. It cannot see other team members' bookings. When you ask, it can book an appointment on your schedule or create a booking type; the app is told to confirm these with you first.

Information the app retrieves is sent to that app and handled under its own privacy policy. Timely365 stores only what is needed to keep the connection working: the app's name and return address, and secured (hashed) access codes, which expire automatically.

You can see and disconnect your apps at any time on the [Connected apps](/allow-access) page. Disconnecting stops access immediately. Changing your password or signing out everywhere also disconnects every app. We do not use information shared through connected apps for advertising or to train AI models.

## Disconnecting Google and deleting data

You can revoke Timely365's access at any time in your [Google Account connections](https://myaccount.google.com/connections). Turning off the Google events display in Timely365 hides those events; it does not by itself revoke Google's authorization or delete stored connection credentials.

To request deletion of stored Google connection credentials, associated data, or your Timely365 account, email [privacy@timely365.com](mailto:privacy@timely365.com) or use the [Timely365 contact form](/contact) and select Privacy / Data Deletion. Revoking access stops future authorized retrieval but does not itself delete information already held by Timely365. We process deletion requests subject to identity verification and applicable retention obligations.

## When we share information

Booking information is shared with the business and authorized staff responsible for your appointment. Information you choose to publish on a public booking page can be viewed by visitors. We use service providers for hosting, communications, payments, and service operations as needed to provide the features you use. We may disclose information when required by law or necessary to protect users and the service. Google data remains subject to the limits described above.

We do not sell personal information. Text-message opt-in information and consent are not shared with third parties or affiliates for their marketing purposes.

## Storage, retention, and security

We retain account and booking information for as long as needed to provide the service, meet legal obligations, resolve disputes, and enforce agreements. App connections end when you disconnect them or after 90 days without use. Google connection credentials are retained to maintain the integration until removed, subject to applicable obligations. You may request deletion using the contact below. Some records may need to be retained for legal or security reasons; backup copies may remain until their normal expiration.

We use safeguards intended to protect information against unauthorized access. No online service can guarantee complete security. Information may be processed in the United States or other locations where our service providers operate.

## Cookies and your choices

Cookies and browser storage support sign-in, sessions, preferences, and connected features. You can manage browser storage through your browser settings; blocking it may affect these features.

Depending on applicable law, you may request access, correction, or deletion of personal information and exercise other applicable privacy rights. Contact us below. For information controlled by a business you booked with, you may also need to contact that business. You can opt out of text messages by replying STOP where supported.

## Children

Timely365 is not intended for children under 13. If you believe a child has provided personal information without appropriate authorization, contact us so we can investigate and address it.

## Changes and contact

We will update the date on this page when this policy changes and provide notice of material changes as appropriate. We will obtain additional consent where required for a new use of Google data.

**Micro Logic LLC**
Privacy: [privacy@timely365.com](mailto:privacy@timely365.com)
Support: [Contact Timely365](/contact)
3115 171st PL SE, Bothell, WA 98012, United States

See also our [Terms of Service](/terms).
`;

export const timelyTerms = `# Timely365 Terms of Service

Last updated: November 3, 2026

These terms govern your use of **Timely365**, operated by **Micro Logic LLC** ("we", "us", or "our"), at [timely365.com](https://timely365.com). By using the service, you agree to these terms. If you use it for a business, you must have authority to act for that business.

## Accounts and responsible use

Provide accurate account information, keep your credentials secure, and use only accounts and business information you are authorized to access. You are responsible for activity you authorize through your account. Notify us if you suspect unauthorized access.

Do not use Timely365 for unlawful activity, spam, harassment, deceptive bookings, unauthorized data collection, or interference with the service. Do not attempt to bypass access controls or upload malicious content.

## Bookings and business responsibilities

Timely365 provides tools to publish availability, accept bookings, manage appointments, and communicate with customers. The business offering an appointment is responsible for its services, prices, availability, cancellation rules, refunds, and fulfillment. Customers should review that business's terms before booking and contact it about appointment changes or disputes.

Businesses are responsible for obtaining the permissions needed to submit customer and staff information and send communications. Keep important appointment information accurate and verify schedules before relying on them.

## Your content and privacy

You retain ownership of the information and content you submit. You grant Micro Logic LLC a limited, non-exclusive permission to host, process, transmit, and display that content as needed to provide and maintain the service and the features you request. You must have the right to submit that content.

Our [Privacy Policy](/privacy) explains how we handle personal information, including Google data. These terms do not grant broader rights to Google user data than the uses described in that policy.

## Google and other integrations

Connecting Google Calendar is optional and requires your authorization. The current integration reads events to display them alongside Timely365 appointments; it does not edit or delete Google events. You can revoke access through your Google Account. External services have their own terms and availability, and integrations may be interrupted or require reconnection.

AI assistants and other apps, such as ChatGPT or Claude, can connect to your account only after you allow them on a Timely365 page. A connected app acts on your behalf, so review what it plans to do before confirming a booking or a new booking type; you are responsible for actions you approve. You can disconnect apps anytime on the [Connected apps](/allow-access) page. Each app is governed by its own terms.

Calendar displays and reminders may be delayed or unavailable. You remain responsible for checking appointment details and maintaining any records your business needs.

## Fees and cancellations

If you purchase a paid Timely365 feature or subscription, the price, billing interval, renewal terms, and applicable cancellation or refund terms shown when you purchase apply. Appointment payments and refunds are subject to the offering business's disclosed policies and applicable law. Nothing here limits rights that applicable law gives you.

## Messages

Where you opt in, appointment-related messages may include confirmations, reminders, schedule changes, and support follow-up. Message frequency varies and message or data rates may apply. Reply STOP to opt out of supported SMS messages and HELP for help. SMS consent is not a condition of purchase. See our [SMS Opt-In Policy](/sms-opt-in).

## Intellectual property

The Timely365 software, branding, and service materials belong to Micro Logic LLC or its licensors. These terms allow you to use the service but do not transfer ownership of that intellectual property. You may not copy or distribute protected materials except as permitted by law or with authorization.

## Availability and termination

We may update the service and suspend or restrict access where necessary to address misuse, security risks, nonpayment, or legal requirements. You may stop using the service and request account deletion using the contact below. Obligations relating to outstanding payments, content rights, liability, and disputes continue where applicable after termination.

## Disclaimers and liability

To the extent permitted by law, the service is provided "as is" and "as available", without warranties of uninterrupted operation, accuracy, or fitness for a particular purpose. Timely365 is not an emergency service.

To the extent permitted by law, Micro Logic LLC is not liable for indirect, incidental, special, or consequential losses arising from use of the service. Our total liability for claims relating to the service will not exceed the amount you paid us for the service in the twelve months before the claim. These limits do not exclude liability or consumer rights that cannot lawfully be excluded or limited.

## Governing law and changes

These terms are governed by the laws of Washington State, United States, subject to mandatory protections under applicable law. Please contact us first to try to resolve a concern.

We may update these terms and will update the date on this page. We will provide notice of material changes as appropriate. If you do not agree to updated terms, stop using the service.

## Contact

**Micro Logic LLC**
Privacy: [privacy@timely365.com](mailto:privacy@timely365.com)
Support: [Contact Timely365](/contact)
3115 171st PL SE, Bothell, WA 98012, United States
`;

export const timelyInfoPages = {
  "/about": { title: "About", content: `# About Timely365

Timely365 helps businesses and individuals organize appointments and make it easier for customers to book a time.

## Your bookings in one place

Create a booking page, publish your services and availability, manage appointments, and coordinate staff schedules. Share your booking link so customers can find a time that works for them.

## See your schedule clearly

View your appointments in a calendar and optionally connect Google Calendar to display your Google events alongside your bookings.

## Get started

[Create your booking page](/booking/start), or [contact Timely365](/contact) for help.

Timely365 is operated by Micro Logic LLC. Read our [Privacy Policy](/privacy) and [Terms of Service](/terms).` },
  "/faq": { title: "Frequently Asked Questions", content: `# Timely365 FAQ

## How do I accept appointments?

Create your business booking page, add your services and availability, then share your booking link with customers.

## Can I see my Google Calendar events?

Yes. Connect Google Calendar from your calendar integrations and authorize read-only access. Google Sign-In and Calendar authorization are separate choices.

## How do I disconnect Google Calendar?

Remove Timely365 in your [Google Account connections](https://myaccount.google.com/connections). Turning off Google events in the calendar only hides them. To request deletion of stored connection data, [contact us](/contact).

## How do I manage my subscription?

Open your business subscription and billing settings to review your plan and the available changes. Your purchase terms describe billing and cancellation.

## Who handles appointment changes and refunds?

Contact the business offering the appointment. Its booking and cancellation policies apply.

## How do I get help or request account deletion?

Use the [Timely365 contact form](/contact). For privacy and account deletion requests, select Privacy / Data Deletion.` },
  "/sms-opt-in": { title: "SMS Opt-In Policy", content: `# Timely365 SMS Opt-In Policy

Timely365 is operated by Micro Logic LLC.

## Your consent

When you opt in, you agree to receive messages from Timely365 regarding appointment reminders, booking confirmations, scheduling updates, service notifications, and customer support follow-up. Message frequency varies. Message and data rates may apply. Consent is not a condition of purchase.

## Stop messages or get help

Reply STOP to opt out or HELP for help. You can also [contact Timely365](/contact).

## Privacy

Text messaging opt-in data and consent will not be shared with third parties or affiliates for their marketing purposes. See our [Privacy Policy](/privacy) and [Terms of Service](/terms).` },
  "/security": { title: "Security and Privacy", content: `# Timely365 Security and Privacy

Timely365 provides account sign-in, business access controls, and optional calendar connections to support appointment scheduling.

## Protect your account

Keep your sign-in credentials private, assign staff access carefully, and remove access when it is no longer needed. Submit only information you are authorized to share.

## Connected calendars

Google Calendar access requires your consent and is read-only. You can revoke access in your Google Account. Our [Privacy Policy](/privacy) describes the information processed and how to request deletion.

## Connected apps

AI assistants such as ChatGPT or Claude can use your Timely365 account only after you sign in and click Allow. They act as you, see only your own bookings, and stop working when you disconnect them on the [Connected apps](/allow-access) page or change your password.

## Sensitive information

Only include information needed to arrange your appointment. Avoid including sensitive personal information in booking notes or support messages. Before submitting information subject to special protection requirements, confirm that the necessary agreements and safeguards are in place for your use.

## Report a concern

[Contact Timely365](/contact) about account security or privacy concerns. Do not include passwords or access tokens in your message.` },
  "/contact": { title: "Contact", content: "" },
};
